/*
 * Product page <-> cart drawer quantity sync.
 *
 * - The product-page quantity box shows how many of the selected variant are in the cart.
 * - Variant NOT in cart: box = quantity to add. "Add to cart" adds that many.
 * - Variant already in cart: changing the box (+ / -) updates the SAME cart line live
 *   (drawer refreshes). "Add to cart" adds 1 more (2 in cart + add 1 = 3, one line).
 * - Changing quantity or removing the item in the cart drawer updates the product page box.
 * - The delivery text property is identical on every add, so lines always merge.
 */

(() => {
  if (window.__pdpQtySyncInitialized) return;
  window.__pdpQtySyncInitialized = true;

  const nativeFetch = window.fetch.bind(window);
  const OLD_SYNC_SELECTOR = '[data-product-quantity-sync]';

  // ---- Neutralize the OLD sync script (it added items without properties => separate line) ----
  function stripOldSync(root = document) {
    root.querySelectorAll(OLD_SYNC_SELECTOR).forEach((input) => {
      input.removeAttribute('data-product-quantity-sync');
    });
  }
  stripOldSync();
  document.addEventListener('DOMContentLoaded', () => stripOldSync());
  // Capture phase runs BEFORE the old script's bubble-phase listener.
  document.addEventListener(
    'change',
    (event) => {
      if (event.target.closest?.(OLD_SYNC_SELECTOR)) stripOldSync();
    },
    true
  );

  // ---- Helpers ----
  const isDrawerInput = (input) => !!input.closest('cart-drawer, cart-drawer-items, cart-items, .cart-item');

  function isPdpInput(input) {
    return (
      input instanceof HTMLInputElement &&
      input.classList.contains('quantity__input') &&
      !isDrawerInput(input)
    );
  }

  function getPdpInputs() {
    return [...document.querySelectorAll('input.quantity__input')].filter(isPdpInput);
  }

  function getVariantId(input) {
    return (
      input.form?.querySelector('[name="id"]')?.value ||
      input.closest('product-info, section')?.querySelector('form[action*="/cart/add"] [name="id"]')?.value ||
      document.querySelector('form[action*="/cart/add"] [name="id"]')?.value ||
      input.dataset.quantityVariantId ||
      ''
    );
  }

  function quantityForVariant(cart, variantId) {
    return (cart?.items || []).reduce(
      (total, item) => (String(item.variant_id) === String(variantId) ? total + Number(item.quantity || 0) : total),
      0
    );
  }

  async function getCart() {
    try {
      const response = await nativeFetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
        cache: 'no-store',
      });
      if (!response.ok) throw new Error(response.status);
      return await response.json();
    } catch (error) {
      console.error('[PDP quantity sync] could not load cart', error);
      return null;
    }
  }

  async function reconcileRejectedQuantity(input, variantId) {
    const liveCart = await getCart();
    if (!liveCart) return;

    const actualQuantity = quantityForVariant(liveCart, variantId);
    const minimum = Math.max(1, Number(input?.getAttribute('min')) || 1);
    if (input) {
      input.value = String(actualQuantity > 0 ? actualQuantity : minimum);
      input.setAttribute('value', input.value);
      input.dataset.cartQuantity = String(actualQuantity);
    }

    const drawer = document.querySelector('cart-drawer');
    if (typeof drawer?.refreshBeforeOpen === 'function') {
      await drawer.refreshBeforeOpen();
    }
  }

  window.GBJReconcileRejectedQuantity = reconcileRejectedQuantity;

  const lastKnown = new WeakMap();
  let timer = null;
  let pending = false;
  let inFlight = false;

  // Cart -> product page display. This is one-way synchronization: drawer
  // changes update the PDP, but PDP +/- clicks remain local until Add to cart.
  async function syncPdpInputs() {
    if (pending || inFlight) return;
    const inputs = getPdpInputs();
    if (!inputs.length) return;

    const cart = await getCart();
    if (!cart || pending || inFlight) return;
    inputs.forEach((input) => {
      const minimum = Math.max(1, Number(input.getAttribute('min')) || 1);
      const variantId = getVariantId(input);
      if (!variantId) return;
      const cartQuantity = quantityForVariant(cart, variantId);
      const displayedQuantity = cartQuantity > 0 ? cartQuantity : minimum;

      input.value = String(displayedQuantity);
      input.setAttribute('value', String(displayedQuantity));
      input.dataset.cartQuantity = String(cartQuantity);
    });

    // Availability messages describe the previous add attempt and are no
    // longer relevant after the cart has been emptied.
    if (Number(cart.item_count || 0) === 0) {
      document.querySelectorAll('product-form').forEach((form) => {
        if (typeof form.handleErrorMessage === 'function') {
          form.handleErrorMessage();
        } else {
          form.querySelector('.product-form__error-message-wrapper')?.setAttribute('hidden', '');
        }
      });
    }
  }

  // Update drawer HTML without opening it
  function renderDrawer(response) {
    const sections = response?.sections;
    if (!sections) return;
    const parser = new DOMParser();

    const drawerHTML = sections['cart-drawer'];
    if (drawerHTML) {
      const source = parser.parseFromString(drawerHTML, 'text/html').querySelector('.drawer__inner');
      const target = document.querySelector('cart-drawer .drawer__inner');
      if (source && target) {
        target.innerHTML = source.innerHTML;
        const drawer = document.querySelector('cart-drawer');
        if (drawer && Number.isFinite(response.item_count)) {
          drawer.classList.toggle('is-empty', response.item_count === 0);
        }
      }
    }

    const bubbleHTML = sections['cart-icon-bubble'];
    if (bubbleHTML) {
      const source = parser.parseFromString(bubbleHTML, 'text/html').querySelector('.shopify-section');
      const target = document.getElementById('cart-icon-bubble');
      if (source && target) target.innerHTML = source.innerHTML;
    }
  }

  // Product page box -> cart (only when the variant is already in the cart)
  async function applyPdpQuantity(input) {
    pending = false;
    inFlight = true;

    try {
      const variantId = getVariantId(input);
      const desired = Math.max(1, parseInt(input.value, 10) || 1);
      if (!variantId) return;

      const cart = await getCart();
      if (!cart) return;

      const lines = cart.items.filter((item) => String(item.variant_id) === String(variantId));
      const current = lines.reduce((total, item) => total + Number(item.quantity || 0), 0);

      if (!lines.length || current === desired) {
        input.dataset.cartQuantity = String(current);
        return;
      }

      const updates = {};
      const difference = desired - current;

      if (difference > 0) {
        updates[lines[0].key] = Number(lines[0].quantity) + difference;
      } else {
        let remove = Math.abs(difference);
        [...lines].reverse().forEach((line) => {
          if (remove === 0) return;
          const removed = Math.min(Number(line.quantity), remove);
          updates[line.key] = Number(line.quantity) - removed;
          remove -= removed;
        });
      }

      const drawer = document.querySelector('cart-drawer');
      const sections = (drawer?.getSectionsToRender?.() || []).map((section) => section.section);
      const sectionList = [...new Set([...sections, 'cart-drawer', 'cart-icon-bubble'])];

      const response = await nativeFetch(`${window.Shopify?.routes?.root || '/'}cart/update.js`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ updates, sections: sectionList, sections_url: window.location.pathname }),
      });
      const state = await response.json();

      if (!response.ok || state.errors) {
        const message =
          state.description ||
          state.message ||
          (typeof state.errors === 'string' ? state.errors : '') ||
          'The requested quantity is not available.';
        await reconcileRejectedQuantity(input, variantId);
        const productForm = input.closest('product-info, section')?.querySelector('product-form');
        productForm?.handleErrorMessage?.(message);

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartError, {
            source: 'product-form',
            productVariantId: variantId,
            errors: state.errors || message,
            message,
          });
        }
        return;
      }

      renderDrawer(state);

      const updatedTotal = quantityForVariant(state, variantId);
      input.dataset.cartQuantity = String(updatedTotal);
      input.value = updatedTotal > 0 ? updatedTotal : Math.max(1, Number(input.getAttribute('min')) || 1);

      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-quantity', productVariantId: variantId, cartData: state });
      }
    } catch (error) {
      console.error('[PDP quantity sync]', error);
    } finally {
      inFlight = false;
      syncPdpInputs();
    }
  }

  // ProductForm uses this when Add to cart is pressed for a variant that is
  // already present. Cancel the delayed change handler and reconcile against
  // the live cart so the selected number is always treated as an exact total.
  window.GBJSetProductCartQuantity = (input) => {
    clearTimeout(timer);
    pending = false;
    return applyPdpQuantity(input);
  };

  document.addEventListener('change', (event) => {
    const input = event.target;
    if (!isPdpInput(input)) return;
    // Quantity changes on the product page are local until form submission.
    // Cart drawer controls continue to update Shopify through cart-drawer.js.
  });

  let syncTimer = null;
  function scheduleSync(delay = 200) {
    clearTimeout(syncTimer);
    syncTimer = setTimeout(syncPdpInputs, delay);
  }

  // Detect ANY cart change made by the theme/apps/custom code (fetch based).
  const CART_CHANGE_PATTERN = /\/cart\/(add|change|update|clear)(\.js)?(?:\?|$)/;
  window.fetch = async function (...args) {
    const response = await nativeFetch(...args);
    try {
      const request = args[0];
      const url = typeof request === 'string' ? request : request instanceof Request ? request.url : '';
      if (response.ok && CART_CHANGE_PATTERN.test(url)) scheduleSync(250);
    } catch (error) {
      /* ignore */
    }
    return response;
  };

  function init() {
    syncPdpInputs();

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      // Drawer qty change / remove / product add => refresh product page box
      subscribe(PUB_SUB_EVENTS.cartUpdate, () => scheduleSync(150));
      // Variant switched => show that variant's cart quantity
      subscribe(PUB_SUB_EVENTS.variantChange, () => scheduleSync(300));
    }

    // Any time the drawer content is re-rendered, re-sync the product page box.
    const drawer = document.querySelector('cart-drawer');
    if (drawer) {
      new MutationObserver(() => scheduleSync(200)).observe(drawer, { childList: true, subtree: true });
    }

    // Safety net: when the tab regains focus.
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) scheduleSync(100);
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();

  // Product sections re-render on variant change: strip old attribute again + resync.
  new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node instanceof Element && (node.matches?.(OLD_SYNC_SELECTOR) || node.querySelector?.(OLD_SYNC_SELECTOR))) {
          stripOldSync();
          return;
        }
      }
    }
  }).observe(document.documentElement, { childList: true, subtree: true });
})();

if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        // Prioritize cart-drawer to open the drawer when adding to cart from product page
        this.cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      // Normalized so the same product always merges into ONE cart line.
      getDeliveryText() {
        const deliveryElement = document.getElementById('delivery-text');
        if (!deliveryElement) return '';

        return (deliveryElement.textContent || '')
          .replace('Estimate Shipping Time:', '')
          .replace(/\s+/g, ' ')
          .trim();
      }

      // Finds the product-page quantity input (never the cart drawer's).
      getQuantityInput() {
        const formId = this.form.id;
        const candidates = [
          formId ? document.querySelector(`input.quantity__input[form="${formId}"]`) : null,
          this.form.querySelector('input.quantity__input'),
          this.closest('product-info')?.querySelector('input.quantity__input'),
          this.closest('section')?.querySelector('input.quantity__input'),
        ];

        return candidates.find((input) => input && !input.closest('cart-drawer')) || null;
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        // Ensure cart element is found, especially if it was initialized later
        this.cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);

        // Remove cart-update style fields so only a normal "add" is sent.
        for (const key of [...formData.keys()]) {
          if (key.startsWith('updates[')) formData.delete(key);
        }

        // The PDP quantity represents the desired total for this variant.
        const qtyInput = this.getQuantityInput();
        const cartQuantity = Math.max(0, Number(qtyInput?.dataset.cartQuantity || 0));
        const qty = parseInt(qtyInput?.value, 10);
        const desiredQuantity = Number.isFinite(qty) && qty > 0 ? qty : 1;

        // For an existing variant, set the cart to the selected total instead
        // of using additive cart/add.js (1 in cart + selected 2 must equal 2,
        // not 3). The PDP +/- controls themselves still make no cart request.
        if (cartQuantity > 0 && qtyInput && typeof window.GBJSetProductCartQuantity === 'function') {
          window.GBJSetProductCartQuantity(qtyInput)
            .then(() => this.cart?.open?.(this.submitButton, true))
            .catch((error) => {
              console.error('[product-form] quantity reconciliation failed:', error);
              this.handleErrorMessage('Something went wrong, please try again.');
            })
            .finally(() => {
              this.submitButton.classList.remove('loading');
              this.submitButton.removeAttribute('aria-disabled');
              this.querySelector('.loading__spinner').classList.add('hidden');
            });
          return;
        }

        formData.set('quantity', desiredQuantity);

        const deliveryText = this.getDeliveryText();
        if (deliveryText) {
          formData.set('properties[Estimate Shipping Time]', deliveryText);
        }

        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.section)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              const errorMessage = response.description || response.message || 'The requested quantity is not available.';
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              const quantityInput = this.getQuantityInput();
              const variantId = formData.get('id');
              if (quantityInput && typeof window.GBJReconcileRejectedQuantity === 'function') {
                window.GBJReconcileRejectedQuantity(quantityInput, variantId)
                  .finally(() => this.handleErrorMessage(errorMessage));
              } else {
                this.handleErrorMessage(errorMessage);
              }

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const hasPerf = typeof CartPerformance !== 'undefined';
            const startMarker = hasPerf ? CartPerformance.createStartingMarker('add:wait-for-subscribers') : null;
            if (!this.error) {
              try {
                publish(PUB_SUB_EVENTS.cartUpdate, {
                  source: 'product-form',
                  productVariantId: formData.get('id'),
                  cartData: response,
                })
                  .then(() => {
                    if (hasPerf) CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
                  })
                  .catch((err) => {
                    console.error('[CartDrawer] A cartUpdate subscriber threw an error (drawer will still open):', err);
                  });
              } catch (err) {
                console.error('[CartDrawer] publish() threw synchronously (drawer will still open):', err);
              }
            }
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    if (hasPerf) {
                      CartPerformance.measure('add:paint-updated-sections', () => {
                        this.cart.renderContents(response);
                      });
                    } else {
                      this.cart.renderContents(response);
                    }
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else if (hasPerf) {
              CartPerformance.measure('add:paint-updated-sections', () => {
                this.cart.renderContents(response);
              });
            } else {
              this.cart.renderContents(response);
            }
          })
          .catch((e) => {
            console.error('[product-form] add to cart failed:', e);
            this.handleErrorMessage('Something went wrong, please try again.');
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            if (typeof CartPerformance !== 'undefined') {
              CartPerformance.measureFromEvent('add:user-action', evt);
            }
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
